import React, { useEffect, useState } from 'react';
import ReactPlayer from 'react-player/lazy';
import { FaDownload } from 'react-icons/fa';
import './Memecollection.scss';
import Header from '../Header';
import Footer from '../Footer/Footer';
import TelegramPopup from '../../Common/TelegramPopup/TelegramPopup';

// Use Webpack's require.context to automatically import memes from the assets folder
function importAll(r) {
  return r.keys().map(r);
}

// This will find all files inside ./assets/ ending with .jpg, .png, or .webm
// Note: This requires a rebuild if files are added/removed in development
let memeFiles = [];
try {
  memeFiles = importAll(require.context('./assets', false, /\.(png|jpe?g|webm|mp4|webp)$/i));
} catch (error) {
  console.error("Could not load memes from ./assets. Ensure the directory exists and files are present.", error);
  // Handle the error appropriately, maybe show a message to the user
}

const Memecollection = () => {
  // Reset scroll position when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // State for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [memesPerPage] = useState(30); // Set items per page

  // Process the imported files into a usable format
  const memes = memeFiles.map((fileSrc, index) => {
    const fileNameWithPossibleHash = fileSrc.split('/').pop();
    const parts = fileNameWithPossibleHash.split('.');
    const format = parts.pop().split('?')[0]; // Extract format, remove query string if present
    const baseName = parts[0]; // Get only the part before the first dot
    
    let type;
    if (['webm', 'mp4'].includes(format.toLowerCase())) {
      type = 'video';
    } else if (['png', 'jpg', 'jpeg', 'webp'].includes(format.toLowerCase())) {
      type = 'image';
    } else {
      type = 'unknown'; // Skip unknown types or handle differently
    }

    // Use filename as a fallback title
    const title = baseName; // Use the corrected base name

    if (type === 'unknown') {
      console.warn(`Skipping file with unknown format: ${fileNameWithPossibleHash}`);
      return null; // Skip rendering this file
    }

    return {
      id: index,
      type: type,
      src: fileSrc,
      title: title, // Keep title for download, derived from filename
      format: format.toLowerCase()
    };
  }).filter(meme => meme !== null); // Filter out null entries (unknown types)

  // Sort memes numerically by title (which holds the base filename ID)
  memes.sort((a, b) => parseInt(a.title, 10) - parseInt(b.title, 10));

  // --- Pagination Logic ---
  // Get current memes
  const indexOfLastMeme = currentPage * memesPerPage;
  const indexOfFirstMeme = indexOfLastMeme - memesPerPage;
  const currentMemes = memes.slice(indexOfFirstMeme, indexOfLastMeme);

  // Calculate total pages
  const totalPages = Math.ceil(memes.length / memesPerPage);

  // Change page handler
  const paginate = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
      window.scrollTo({ top: 0, behavior: 'smooth' }); // Scroll to top on page change
    }
  };
  // --- End Pagination Logic ---

  // --- Pagination Display Logic ---
  const getPageNumbersToShow = (currentPage, totalPages) => {
    const maxPageButtons = 5; // Max number of page number buttons to show
    const pageNumbers = [];

    if (totalPages <= maxPageButtons) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      // Calculate start and end page for the sliding window
      let startPage, endPage;
      if (currentPage <= 3) {
        // Near the start
        startPage = 1;
        endPage = maxPageButtons;
      } else if (currentPage >= totalPages - 2) {
        // Near the end
        startPage = totalPages - maxPageButtons + 1;
        endPage = totalPages;
      } else {
        // In the middle
        startPage = currentPage - 2;
        endPage = currentPage + 2;
      }

      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }
    }
    return pageNumbers;
  };
  // --- End Pagination Display Logic ---

  const handleDownload = (src, title, format) => {
    const link = document.createElement('a');
    link.href = src;
    // Use the derived title for the download filename
    link.download = `${title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.${format}`; 
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="memecollection-page" data-scroll-root>
      <Header />

      <main className="memecollection-content">
        <section className="memecollection-hero">
          <div className="container">
            <h1>Reddicks Meme Collection</h1>
            <p className="subtitle">Community creations fueling the Reddicks & Radix hype!</p>
          </div>
        </section>

        <section className="meme-gallery-section">
          <div className="container">
            <h2 className="section-title">Gallery of Greatness</h2>
            {memes.length > 0 ? (
              <div className="meme-grid">
                {currentMemes.map((meme) => (
                  <div key={meme.id} className="meme-card">
                    <div className="meme-media">
                      {meme.type === 'image' ? (
                        <img src={meme.src} alt={`Reddicks Meme ${meme.title}`} loading="lazy" />
                      ) : (
                        <ReactPlayer
                          url={meme.src}
                          controls={true}
                          loop={true}
                          muted={true}
                          playsInline={true}
                          width="100%"
                          height="100%"
                          config={{ file: { attributes: { preload: 'metadata' } } }}
                        />
                      )}
                    </div>
                    <div className="meme-info">
                      {/* Display title for all meme types */}
                      <span className="meme-id">ID: {meme.title}</span>
                      <button 
                        className="download-button" 
                        onClick={() => handleDownload(meme.src, meme.title, meme.format)}
                        aria-label="Download Meme"
                      >
                        <span className="button-text">Download</span>
                        <FaDownload className="button-icon" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="gallery-note">No memes found in the collection yet. Add some to the assets folder!</p>
            )}
             <p className="gallery-note">More memes coming soon!</p>
             {/* Pagination Controls */}
             {totalPages > 1 && (
               <div className="pagination-controls">
                 {/* First Page Button */}
                 <button
                   onClick={() => paginate(1)}
                   disabled={currentPage === 1}
                   className="pagination-button first-button"
                   aria-label="Go to first page"
                 >
                   &laquo; {/* << */}
                 </button>

                 {/* Previous Button */}
                 <button
                   onClick={() => paginate(currentPage - 1)}
                   disabled={currentPage === 1}
                   className="pagination-button prev-button"
                   aria-label="Go to previous page"
                 >
                   &lsaquo; {/* < */}
                 </button>

                 {/* Page Numbers */}
                 {getPageNumbersToShow(currentPage, totalPages).map(number => (
                   <button
                     key={number}
                     onClick={() => paginate(number)}
                     className={`pagination-button page-number ${currentPage === number ? 'active' : ''}`}
                     aria-current={currentPage === number ? 'page' : undefined}
                   >
                     {number}
                   </button>
                 ))}

                 {/* Next Button */}
                 <button
                   onClick={() => paginate(currentPage + 1)}
                   disabled={currentPage === totalPages}
                   className="pagination-button next-button"
                   aria-label="Go to next page"
                 >
                   &rsaquo; {/* > */}
                 </button>

                 {/* Last Page Button */}
                 <button
                   onClick={() => paginate(totalPages)}
                   disabled={currentPage === totalPages}
                   className="pagination-button last-button"
                   aria-label="Go to last page"
                 >
                   &raquo; {/* >> */}
                 </button>
               </div>
             )}
          </div>
        </section>
      </main>

      <Footer />
      <TelegramPopup />
    </div>
  );
};

export default Memecollection; 
